// Unified gateway software version management (docs/GatewayVersionManagement.md).
// One control for both container and VM (ISO) gateways: pick a policy
// (automatic / pinned / hold) and, when pinned, a specific published version.
// SaaS-only endpoints (gateway_releases / firmware_images live behind the
// firmware feature); callers should gate on useFeature('firmware').
import axios from 'axios';
import { config } from '@/config.ts';

const backendApi = config.backendApi;

export type UpdatePolicy = 'auto' | 'pinned' | 'hold';

/** Live install progress, present only while one is in flight. */
export interface GatewayUpdateProgress {
  version: string;
  /** Stable id: 'preparing' | 'downloading' | 'verifying' | 'installing' |
   *  'cleaning_up' | 'restarting' | 'succeeded' | 'failed'. Key off this. */
  phase: string;
  /** Human wording authored by the gateway, so the UI owns no vocabulary. */
  label: string | null;
  detail: string | null;
  /** 0-100, or null when the download total is unknown — never read as 0. */
  percent: number | null;
  at: string;
}

export interface GatewaySoftwareVersions {
  policy: UpdatePolicy;
  pinnedVersion: string | null;
  runningVersion: string | null;
  statusPhase: string | null;
  statusVersion: string | null;
  statusError: string | null;
  /** `null` while nothing is installing — and for a gateway too old to report. */
  progress: GatewayUpdateProgress | null;
  availableVersions: string[];
  deploymentKind: string;
}

/** How long a progress message counts as current. A gateway sends one every 3 s
 *  while it installs; the rest of the margin covers its clock disagreeing with
 *  ours, since `at` is stamped by the gateway. */
export const LIVE_PROGRESS_MS = 2 * 60_000;

const IN_FLIGHT = new Set(['preparing', 'downloading', 'verifying', 'installing', 'cleaning_up', 'restarting']);

/**
 * The verdict says failed, but the gateway is visibly installing that version
 * again: a retry. The retry's own update check reported the attempt before it,
 * so the verdict reads failed until the install finishes, and the panel shows
 * what the gateway is doing instead. Display only — the verdict, and the
 * incident it raises, stay the reconciler's (docs/GatewayUpdateProgress.md).
 *
 * `now` is when the data was fetched, not when it renders.
 */
export function retryInFlight(
  versions: Pick<GatewaySoftwareVersions, 'statusPhase' | 'statusVersion' | 'progress'>,
  now: number,
): boolean {
  const progress = versions.progress;
  if (versions.statusPhase !== 'failed' || !progress || !IN_FLIGHT.has(progress.phase)) return false;
  if (progress.version !== versions.statusVersion) return false;
  return now - Date.parse(progress.at) < LIVE_PROGRESS_MS;
}

export const fetchGatewaySoftwareVersions = async (gatewayId: string): Promise<GatewaySoftwareVersions> => {
  const response = await axios.get<GatewaySoftwareVersions>(
    `${backendApi}/api/gateways/${gatewayId}/software-versions`,
    { withCredentials: true },
  );
  return response.data;
};

export interface SetVersionArgs {
  gatewayId: string;
  policy: UpdatePolicy;
  version?: string;
}

export const setGatewaySoftwareVersion = async ({ gatewayId, policy, version }: SetVersionArgs): Promise<void> => {
  await axios.post(
    `${backendApi}/api/gateways/${gatewayId}/software-version`,
    { policy, version },
    { withCredentials: true },
  );
};
